
import { v4 as uuid } from 'uuid';
import { UniForm } from '../dao/UniForm';
import { UniInput } from "../dao/UniInput";
import { UniOption } from "../dao/UniOption";
import { UniQuestion } from "../dao/UniQuestion";
import { HisRule, SpwkUtilV3 } from '../dao/SpwkUtilV3';
import { new_ErrorAlert } from './ExportManager';
import _ from 'lodash';


/**
 * 定義資料類型
 */
export enum SpwkMetaData_DataType {
  Unknown = '',
  Form = '表單',
  Question = '題目',
  Option = '選項',
  Input = '輸入'
}


/** 
 * 將介面 Excel 化，且最後留下一個填 Value 的欄位
 * 之後可依照此 Excel 依照規則 匯出 HIS格式 或 申報格式
*/
export class SpwkMetaData {

  IsHisBasicMeta(): boolean {
    return (this.q_his_rule.startsWith("{") && this.q_his_rule.endsWith("}"));
  }

  GetRhfValue(rhf_data: any): any {
    return GetRhfValueCore(rhf_data, this.rhf_name);
  }

  /** 判斷此項目是否為 UniOption */
  IsUniOption(obj: UniForm | UniQuestion | UniOption | UniInput | null): obj is UniOption {
    if (obj == null) {
      return false;
    }
    return 'v3_group' in obj;
  }

  /** 目前會產生 guid 確保不重複，讓 MUI DataGrid 顯示時不會出錯 */
  id: string = "";

  /** 特作代碼 */
  func_code: string = "";

  /** 定義檔: UniQuestion | UniOption | UniInput... */
  data_obj: UniForm | UniQuestion | UniOption | UniInput | null = null;

  /** 定義檔.資料類型 "" | "UniQuestion" | "UniOption" | "UniInput" */
  data_type: SpwkMetaData_DataType = SpwkMetaData_DataType.Unknown;

  /** 定義檔.serno */
  data_serno: number = 0;

  /** 定義檔.guid */
  data_guid: string = "";

  /** 前端項目顯示所屬的Group */
  v3_group: string = "";

  /** 連動觸發項目 */
  triggers: string = "";

  /** 拆分項目guid */
  split_from_guid: string = "";
  /** 是否為拆分父項 */
  is_split_parent: boolean = false;

  //---------------------------------------------------------- 

  /** question、option、input 應該要有 question_guid */
  question_guid: string = "";

  /** option、input 應該要有 option_guid */
  option_guid: string = "";

  /** 基本資料、作業經歷、既往病史... */
  chapter: string = "";

  /** 題目類型: 單選、多選、非選、標題... */
  kind: string = "";


  q_his_rule: HisRule = HisRule.None;
  o_his_rule: HisRule = HisRule.None;
  i_his_rule: HisRule = HisRule.None;

  /** 各 data_obj 的 code */
  code: string = "";

  /* 此 Row 是否需要填寫數值 (input、多選的選項、單選的題目 應該填寫數值，其他則不需要) */
  is_need_value = false;

  /** rhf 讀取數值的 path */
  rhf_name = "";

  /** rhf 填寫的數值 (問卷網頁 受檢者填寫的原始資料) */
  rhf_value: any = "";

  /** 轉院內HIS格式用的代碼... */
  his_code: string = "";


  q_gov_code: string = "";// 申報 題目代碼
  o_gov_code: string = "";// 申報 選項代碼
  i_gov_code: string = "";// 申報 選項代碼



  constructor(func_code: string, data_obj: UniForm | UniQuestion | UniOption | UniInput) {

    this.id = uuid();// 產生隨機的 guid 讓 MUI.DataGrid id不重複

    this.func_code = func_code;// A01、A98、B01... 作業代碼(外部統一指定)

    //#region 綁定 data_object
    this.data_obj = data_obj;// 對映的物件

    this.data_type = SpwkMetaData.GetDataType(data_obj);// 物件 Class
    this.data_serno = data_obj.serno;
    this.data_guid = data_obj.guid;
    //#endregion

    if (this.data_type === SpwkMetaData_DataType.Question) {
      let question = data_obj as UniQuestion;
      this.InitByQuestion(question);

    } else if (this.data_type === SpwkMetaData_DataType.Option) {
      let option = data_obj as UniOption;
      this.InitByOption(option);

    } else if (this.data_type === SpwkMetaData_DataType.Input) {
      let input = data_obj as UniInput;
      this.InitByInput(input);

    } else if (this.data_type === SpwkMetaData_DataType.Form) {
      let form = data_obj as UniForm;
      this.InitByForm(form, func_code);

    } else {
      alert("6354313 未知的 data_type:" + this.data_type + " !!!");
      throw new Error("6354313 未知的 data_type:" + this.data_type + " !!!");
    }

  }


  InitByForm(form: UniForm, func_code: string) {
    //#region Form - 虛擬一筆資料 用來代表 Form
    this.code = "### 問卷: " + func_code + " ###";
    //#endregion
  }




  InitByQuestion(question: UniQuestion) {
    this.question_guid = question.guid;
    this.option_guid = "";// 無須指定 選項GUID

    this.chapter = question.chapter;
    this.kind = question.kind;

    this.code = question.question_code;

    this.q_his_rule = question.q_his_rule;

    //#region  this.is_need_value = ? 
    if (this.kind === SpwkUtilV3.Q_KIND_單選) {
      this.is_need_value = true;// 單選的題目，需要填寫數值
    } else {
      this.is_need_value = false;// 非選、標題、多選 不用填答案在此
    }
    //#endregion

    //#region this.rhf_name = ?
    if (this.is_need_value) {
      const rhf_name = question.get_Question_RhfName();// 單選的 rhf_name
      this.rhf_name = rhf_name;
    }
    //#endregion

    this.his_code = question.q_his_code;

    this.q_gov_code = question.q_gov_code;

  }


  InitByOption(option: UniOption) {
    this.question_guid = option.question_guid;// parent_question
    this.option_guid = option.guid;

    this.chapter = option.question_chapter ?? "";// parent.chapter
    this.kind = option.question_kind ?? "";// parent.kind 用來判斷此選項是單選或多選

    if (option.OwnerQuestion == null) {
      throw new_ErrorAlert("4341242 缺少 OwnerQuestion !!!");
    } else {
      this.q_his_rule = option.OwnerQuestion.q_his_rule;
    }

    this.code = option.option_code;

    //#region  this.is_need_value = ? 
    if (this.kind === SpwkUtilV3.Q_KIND_多選) {
      this.is_need_value = true;// 多選的選項，需要填寫 rhf 數值
    } else {
      this.is_need_value = false;
    }
    //#endregion

    //#region this.rhf_name = ?
    if (this.is_need_value === true) {
      const chk_name = option.get_Option_RhfName();
      this.rhf_name = chk_name;
    }
    //#endregion

    this.his_code = option.o_his_code;

    this.o_gov_code = option.o_gov_code;

    this.v3_group = option.v3_group;
    this.triggers = option.triggers;
    this.is_split_parent = option.is_split_parent ? true : false;
    this.split_from_guid = option.split_from_option_guid;
  }

  InitByInput(input: UniInput) {
    this.question_guid = input.question_guid ?? "";// parent
    this.option_guid = input.option_guid ?? "";// parent

    this.chapter = input.question_chapter ?? "";// parent.chapter
    this.kind = input.question_kind ?? "";// parent.kind 用來判斷此選項是單選或多選

    if (input.OwnerOption == null) {
      throw new_ErrorAlert("4341242 缺少 OwnerQuestion !!!");
    } else {
      // this.q_his_rule = input.OwnerOption.o_his_rule;

      if (input.OwnerOption.OwnerQuestion == null) {
        throw new_ErrorAlert("684626 缺少 OwnerQuestion !!!");
      } else {
        this.q_his_rule = input.OwnerOption.OwnerQuestion.q_his_rule;
      }

    }



    this.code = input.input_code;

    this.is_need_value = true;// input 總是需要填寫數值

    if (this.is_need_value === true) {
      const rhf_name = input.OwnerOption?.get_Input_RhfName(input.input_code);
      this.rhf_name = rhf_name ?? "";
    }

    this.i_his_rule = input.i_his_rule;
    this.his_code = input.i_his_code;

    this.i_gov_code = input.i_gov_code;

  }

  /** 
   * 方便 Debug 的文字，模擬樹狀結構
   */
  getDebugDisplayCode(): any {

    if (this.data_type === SpwkMetaData_DataType.Question) {
      if (this.kind === SpwkUtilV3.Q_KIND_標題) {
        let q = (this.data_obj as UniQuestion);
        return "標題: " + q.question_title;
      } else {
        return this.code;
      }


    } else if (this.data_type === SpwkMetaData_DataType.Option) {

      let option = this.data_obj as UniOption;

      let re = ". . └─ ";

      if (this.kind === "多選") {
        re += (this.rhf_value === true ? "☑" : "□") + " " + this.code;
      } else if (this.kind === "單選") {
        re += "○ " + this.code;
        // re += (this.rhf_value === true ? "●" : "○") + " " + this.code;
      } else if (this.kind === "非選") {
        re += this.code;
      } else {
        // pass;
      }

      if (option.split_from_option_guid !== "") {
        re = "(拆). . " + re;
      }

      return re;

    } else if (this.data_type === SpwkMetaData_DataType.Input) {

      return ". . . . └─ {__" + this.code + "__}";

    } else if (this.data_type === SpwkMetaData_DataType.Form) {
      return this.code;
    }

  }


  /**
  * gpt:取得此物件是什麼類型
  */
  static GetDataType(data_obj: UniForm | UniQuestion | UniOption | UniInput): SpwkMetaData_DataType {
    if (data_obj instanceof UniOption) {
      return SpwkMetaData_DataType.Option;
    } else if (data_obj instanceof UniQuestion) {
      return SpwkMetaData_DataType.Question;
    } else if (data_obj instanceof UniInput) {
      return SpwkMetaData_DataType.Input;
    } else if (data_obj instanceof UniForm) {
      return SpwkMetaData_DataType.Form;
    } else {
      alert("匯出中繼檔.GetDataType():: ERROR !!!");
      return SpwkMetaData_DataType.Unknown;
    }
  }


}


/**
* 依照 rhf_name 讀取資料
* ref: https://stackoverflow.com/questions/6393943/convert-a-javascript-string-in-dot-notation-into-an-object-reference
* 2021 You don't need to pull in another dependency...
*/
function GetRhfValueCore(ans_rhf_data: any, rhf_name: string) {
  const value = _.get(ans_rhf_data, rhf_name);
  if (typeof value === 'string') {
    return value.replace(/,/g, '，');
  }
  return value;
}





// function GetHisRuleByQuestion(question: UniQuestion, chapter: string, kind: string): string {

//   if()

//   let re = "rule...";

//   return re;
// }

